import { Archive, CloudOff, Copy, Download, Info, Send, Trash, Upload, WandSparkles } from 'lucide-react'
import { useRef, useState, type DragEvent } from 'react'
import {
  uniqError,
  uploadBatch,
  uploadKey,
  useDeleteUniqJob,
  useSendUniqToChat,
  useUniqJobs,
  useUniqRefresh,
  useUploads,
  type UniqCopy,
  type UniqFile,
  type UniqJob,
  type UniqLimits,
  type UploadState,
} from '../api/uniq'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { copyOrShow } from '../components/ManualCopy'
import { PageHeader } from '../components/PageHeader'
import { Spinner } from '../components/Spinner'
import { toast } from '../components/Toast'
import { downloadFile } from '../lib/download'
import { formatTimecode, plural } from '../lib/format'
import { useIsDesktop } from '../lib/useIsDesktop'
import { isInTelegram } from '../telegram'
import { useViewState } from '../lib/navigation'

const DEFAULT_LIMITS: UniqLimits = {
  max_file_bytes: 1024 ** 3,
  max_batch_bytes: 5 * 1024 ** 3,
  max_files: 10,
  max_copies: 5,
  default_copies: 3,
  extensions: ['.mp4', '.mov', '.m4v', '.webm', '.mkv'],
}

const num1 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 })

function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${num1.format(n / 1024 ** 3)} ГБ`
  if (n >= 1024 ** 2) return `${num1.format(n / 1024 ** 2)} МБ`
  return `${Math.max(1, Math.round(n / 1024))} КБ`
}

/** Имя ролика без расширения: из него имена копий и архива, как у сервера. */
function stemOf(name: string): string {
  const i = name.lastIndexOf('.')
  return (i > 0 ? name.slice(0, i) : name) || 'video'
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.')
  return i >= 0 ? name.slice(i).toLowerCase() : ''
}

/** Те же лимиты, что на сервере: отказ сразу, без загрузки гигабайта впустую. */
function checkFiles(files: File[], limits: UniqLimits): string | null {
  if (files.length === 0) return null
  if (files.length > limits.max_files) return `За раз — не больше ${limits.max_files} файлов`
  for (const f of files) {
    if (!limits.extensions.includes(extOf(f.name))) return `${f.name}: нужен MP4, MOV или WebM`
    if (f.size === 0) return `${f.name}: пустой файл`
    if (f.size > limits.max_file_bytes) return `${f.name}: больше 1 ГБ`
  }
  const total = files.reduce((s, f) => s + f.size, 0)
  if (total > limits.max_batch_bytes) return 'За раз — не больше 5 ГБ'
  return null
}

export function UniquenessPage() {
  const desktop = useIsDesktop()
  const jobs = useUniqJobs()
  const refresh = useUniqRefresh()
  const limits = jobs.data?.limits ?? DEFAULT_LIMITS
  const [copies, setCopies] = useViewState<number | null>('uniqueness.copies', null)
  const count = copies ?? limits.default_copies
  const [starting, setStarting] = useState(false)
  const list = jobs.data?.jobs ?? []

  async function onFiles(files: File[]) {
    const problem = checkFiles(files, limits)
    if (problem) {
      toast(problem, { error: true, ms: 3500 })
      return
    }
    if (files.length === 0) return
    setStarting(true)
    try {
      const res = await uploadBatch(files, count, refresh)
      if (res.failed > 0) toast(`Не загрузилось: ${res.failed} из ${files.length}`, { error: true })
      else toast(res.uploaded === 1 ? 'Ролик в очереди' : 'Ролики в очереди')
    } catch (err) {
      toast(uniqError(err), { error: true, ms: 3500 })
    } finally {
      setStarting(false)
      refresh()
    }
  }

  return (
    <>
      <PageHeader
        title="Уникализация"
        description="Загрузите ролики, которые уже выходили в ленте. Вернём копии, которые площадка не свяжет с оригиналом, и подпись к каждой."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted">Копий на ролик</span>
        <div role="radiogroup" aria-label="Копий на ролик" className="inline-flex rounded-lg bg-surface p-0.5 ring-1 ring-line ring-inset">
          {Array.from({ length: limits.max_copies }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={n === count}
              onClick={() => setCopies(n)}
              className={`h-9 min-w-10 rounded-md px-2 text-sm font-medium tabular-nums transition-colors desk:h-7 desk:min-w-8 ${
                n === count ? 'bg-card text-fg shadow-sm ring-1 ring-line' : 'text-muted hover:text-fg'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <Dropzone onFiles={(f) => void onFiles(f)} disabled={starting} />

      <p className="mt-2 flex items-start gap-1.5 text-xs text-muted">
        <Info size={14} className="mt-px shrink-0" />
        Только свои ролики: копии для пробных Reels. Файлы хранятся 48 часов.
        {jobs.data && !jobs.data.captions_enabled && ' Подписи Codex доступны владельцу после настройки входа; под копиями будет первая фраза ролика.'}
      </p>

      <div className="mt-5">
        {jobs.isPending ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : jobs.isError ? (
          <EmptyState icon={CloudOff} title="Не удалось загрузить задания">
            <Button className="mt-3" onClick={() => void jobs.refetch()}>
              Повторить
            </Button>
          </EmptyState>
        ) : list.length === 0 ? (
          <EmptyState icon={WandSparkles} title="Пока пусто">
            {desktop ? 'Перетащите ролики в область выше.' : 'Выберите ролики из галереи.'}
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-4">
            {list.map((job) => (
              <JobBlock key={job.id} job={job} sendEnabled={jobs.data.send_enabled} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function Dropzone({ onFiles, disabled }: { onFiles: (files: File[]) => void; disabled: boolean }) {
  const input = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  // Перетаскивать можно только на компьютере; на телефоне — выбор из галереи.
  const desktop = useIsDesktop()

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setOver(false)
    if (!disabled) onFiles(Array.from(e.dataTransfer.files))
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`flex flex-col items-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors desk:py-10 ${
        over ? 'border-fg bg-surface' : 'border-line bg-card'
      }`}
    >
      <span className="mb-3 inline-flex size-11 items-center justify-center rounded-xl bg-surface text-muted ring-1 ring-line ring-inset">
        <Upload size={20} />
      </span>
      {desktop && <div className="font-medium">Перетащите видео сюда</div>}
      <Button
        variant="primary"
        className={desktop ? 'mt-3' : ''}
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        {disabled ? <Spinner size={16} className="text-accent-fg" /> : <Upload size={16} />}
        {desktop ? 'Выберите файлы' : 'Выбрать из галереи'}
      </Button>
      <p className="mt-2 text-xs text-muted">MP4, MOV, WebM. До 1 ГБ на файл, до 5 ГБ за раз.</p>
      <input
        ref={input}
        type="file"
        accept="video/*,.mkv"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.currentTarget.files ?? [])
          e.currentTarget.value = ''
          onFiles(files)
        }}
      />
    </div>
  )
}

function hoursLeft(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 3_600_000))
}

function JobBlock({ job, sendEnabled }: { job: UniqJob; sendEnabled: boolean }) {
  const remove = useDeleteUniqJob()
  const [confirm, setConfirm] = useState(false)
  const left = hoursLeft(job.expires_at)

  return (
    <section className="rounded-xl bg-card ring-1 ring-line">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2 desk:px-4">
        <div className="min-w-0 flex-1 text-xs text-muted">
          {job.files.length} {plural(job.files.length, 'ролик', 'ролика', 'роликов')} · {job.copies}{' '}
          {plural(job.copies, 'копия', 'копии', 'копий')} · удалится через {left} ч
        </div>
        {confirm ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
              Отмена
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(job.id, { onError: (err) => toast(uniqError(err), { error: true }) })
              }
            >
              Удалить
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" aria-label="Удалить" onClick={() => setConfirm(true)}>
            <Trash size={14} />
            <span className="hidden desk:inline">Удалить</span>
          </Button>
        )}
      </header>
      <div className="divide-y divide-line">
        {job.files.map((file) => (
          <FileCard key={file.id} job={job} file={file} sendEnabled={sendEnabled} />
        ))}
      </div>
    </section>
  )
}

const STAGE: Record<string, string> = {
  probe: 'Проверяем файл',
  captions: 'Пишем подписи',
  copies: 'Собираем копии',
}

function statusLine(job: UniqJob, file: UniqFile, upload: UploadState | undefined): { text: string; progress: number | null; error?: boolean } {
  switch (file.status) {
    case 'uploading':
      if (upload?.error) return { text: `Не загрузилось: ${uniqError(upload.error)}`, progress: null, error: true }
      if (upload && !upload.done) {
        const p = upload.total ? upload.loaded / upload.total : 0
        return { text: `Загрузка ${Math.round(p * 100)} %`, progress: p }
      }
      return { text: 'Загрузка не завершена', progress: null, error: true }
    case 'uploaded':
      return { text: 'Загружен, ждёт остальные файлы', progress: null }
    case 'queued':
      return {
        text: job.queue_position ? `В очереди, перед ним ${job.queue_position}` : 'В очереди',
        progress: null,
      }
    case 'waiting':
      return { text: 'Ждёт своей очереди в партии', progress: null }
    case 'processing':
      return { text: `${STAGE[file.stage ?? ''] ?? 'Обработка'} · ${Math.round(file.progress * 100)} %`, progress: file.progress }
    case 'failed':
      return { text: file.error ?? 'Не удалось обработать', progress: null, error: true }
    case 'skipped':
      return { text: file.error ?? 'Файл не загрузился', progress: null, error: true }
    case 'done':
      return { text: 'Готово', progress: null }
  }
}

function FileCard({ job, file, sendEnabled }: { job: UniqJob; file: UniqFile; sendEnabled: boolean }) {
  const uploads = useUploads()
  const upload = uploads.get(uploadKey(job.id, file.id))
  const status = statusLine(job, file, upload)
  const send = useSendUniqToChat()
  const tg = isInTelegram()

  return (
    <div className="px-3 py-3 desk:px-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{file.name}</div>
          <div className={`text-sm ${status.error ? 'text-danger' : 'text-muted'}`}>
            <span className="tabular-nums">
              {formatBytes(file.size)}
              {file.duration ? ` · ${formatTimecode(file.duration)}` : ''}
            </span>
            {file.status !== 'done' && ` · ${status.text}`}
          </div>
        </div>
        {file.status === 'done' && file.copies.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tg && sendEnabled && (
              <Button
                variant="primary"
                size="sm"
                disabled={send.isPending}
                onClick={() =>
                  send.mutate(
                    { jobId: job.id, fileId: file.id },
                    {
                      onSuccess: (r) => {
                        const total = r.sent + r.links
                        const links = r.links > 0 ? `, ${r.links} — ссылкой (больше предела бота)` : ''
                        toast(`В чате с ботом: ${total} ${plural(total, 'копия', 'копии', 'копий')}${links}`, { ms: 3500 })
                      },
                      onError: (err) => toast(uniqError(err), { error: true }),
                    },
                  )
                }
              >
                {send.isPending ? <Spinner size={14} className="text-accent-fg" /> : <Send size={14} />}
                Отправить в чат
              </Button>
            )}
            {file.zip_url && (
              <Button size="sm" onClick={() => downloadFile(file.zip_url!, `${stemOf(file.name)}-kopii.zip`)}>
                <Archive size={14} />
                Скачать всё (zip)
              </Button>
            )}
          </div>
        )}
      </div>

      {status.progress !== null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface" role="progressbar" aria-valuenow={Math.round(status.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-fg transition-[width] duration-500" style={{ width: `${Math.max(2, status.progress * 100)}%` }} />
        </div>
      )}

      {file.status === 'done' && file.captions_note && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-muted">
          <Info size={14} className="mt-px shrink-0" />
          {file.captions_note}
        </p>
      )}

      {file.copies.length > 0 && (
        <ul className="mt-3 grid gap-3 desk:grid-cols-3 xl:grid-cols-5">
          {file.copies.map((copy) => (
            <CopyCard key={copy.n} copy={copy} stem={stemOf(file.name)} />
          ))}
        </ul>
      )}
    </div>
  )
}

function CopyCard({ copy, stem }: { copy: UniqCopy; stem: string }) {
  return (
    <li className="flex gap-3 rounded-lg bg-surface p-2 ring-1 ring-line ring-inset desk:flex-col">
      <video
        src={copy.url}
        controls
        playsInline
        preload="metadata"
        className="aspect-[9/16] w-28 shrink-0 rounded-md bg-black object-contain desk:w-full"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2 text-xs text-muted">
          <span className="font-medium text-fg">Копия {copy.n}</span>
          <span className="tabular-nums">{formatBytes(copy.size)}</span>
        </div>
        {copy.caption ? (
          <p className="text-sm whitespace-pre-line text-pretty">{copy.caption}</p>
        ) : (
          <p className="text-sm text-muted">Без подписи</p>
        )}
        <p className="text-[11px] leading-snug text-muted" title="Чем копия отличается от оригинала">
          {copy.summary}
        </p>
        <div className="mt-auto flex flex-wrap gap-2">
          {copy.caption && (
            <Button
              size="sm"
              onClick={() => void copyOrShow(copy.caption, { done: 'Подпись скопирована', title: 'Подпись к копии' })}
            >
              <Copy size={14} />
              Копировать
            </Button>
          )}
          <Button size="sm" onClick={() => downloadFile(copy.download_url, `${stem}-${copy.n}.mp4`)}>
            <Download size={14} />
            Скачать
          </Button>
        </div>
      </div>
    </li>
  )
}
